import type { WithingsMeasurement } from "@/domains/health/withings/types/database";

import { core } from "@tauri-apps/api";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import React, { useEffect, useState } from "react";

// WHAT: Health-Dashboard-Karte zur Anzeige aktueller Withings-Körpermetriken (Gewicht, BMI, Muskelmasse) inkl. Trends
// WHY:  Integriert Withings-Messungen in das Health-Workspace-Dashboard und bietet einen schnellen Überblick über Körperdaten
// DOMAIN: health → dashboard → molecules
// DEPENDENCIES: Tauri `core.invoke('get_withings_measurements')`, `WithingsMeasurement`-Typ, TracQ `Card`, lucide-react Icons
// RISKS:
//   - NOTE: Trendfunktionen erwarten mehr als eine Messung; bei nur einem Wert werden aktuell Platzhalter genutzt
//   - NOTE: Die aktuelle Implementierung verwendet denselben Wert als „aktuell“ und „vorher“ in `getTrendIcon`/`getTrendColor`
import { Card } from "@/components/ui/Card";

interface WithingsMetrics {
  bmi: null | number;
  lastUpdate: null | string;
  muscleMass: null | number;
  weight: null | number;
}

/**
 * WHAT:
 *   Lädt die letzten Withings-Messungen, extrahiert aktuelle Werte für Gewicht, BMI und Muskelmasse
 *   und stellt sie in einer kompakten Card mit Lade-/Fehlerzuständen dar.
 *
 * WHY:
 *   Erweitert das Health-Dashboard um Körperzusammensetzungsdaten und erlaubt schnelles Monitoring
 *   ohne in eine dedizierte Withings-Ansicht zu wechseln.
 *
 * BEHAVIOR:
 *   - Ruft beim Mount `get_withings_measurements` (limit 3) via `core.invoke` auf.
 *   - Nimmt die neueste Messung als aktuelle Anzeige und versucht, daraus einfache Trendinformationen abzuleiten.
 *   - Zeigt Lade-Skeleton, Fehlermeldung mit Retry sowie eine Update-Schaltfläche innerhalb der Card.
 *
 * RISKS:
 *   - NOTE: Die Trendberechnung ist aktuell rudimentär; für echte Trends sollten Differenzen auch visualisiert werden.
 *   - NOTE: Keine Paginierung/Detailansicht für historische Werte – nur die letzte Messung wird berücksichtigt.
 *
 * @returns {JSX.Element} Withings metrics card component
 *
 */
export const WithingsMetricsCard: React.FC = () => {
  const [metrics, setMetrics] = useState<WithingsMetrics>({
    bmi: null,
    lastUpdate: null,
    muscleMass: null,
    weight: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);

  useEffect(() => {
    void loadWithingsMetrics();
    // Reason: loadWithingsMetrics is stable and should only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadWithingsMetrics is stable
  }, []);

  /**
   * WHAT:
   *   Processes measurements and extracts current metrics.
   *
   * WHY:
   *   Separates metric extraction logic to reduce cognitive complexity.
   *
   * @param latest - Latest measurement
   *
   * @param previous - Previous measurement (optional)
   *
   * @returns Processed metrics
   *
   */
  const processMeasurements = (
    latest: WithingsMeasurement,
    previous?: WithingsMeasurement,
  ): WithingsMetrics => {
    const currentMetrics: WithingsMetrics = {
      bmi: latest.bmi,
      lastUpdate: latest.measurement_date,
      muscleMass: latest.muscle_mass_kg,
      weight: latest.weight_kg,
    };

    // Trend-Berechnung (currently just uses latest values)
    if (previous) {
      if (latest.weight_kg && previous.weight_kg) {
        currentMetrics.weight = latest.weight_kg;
      }
      if (latest.bmi && previous.bmi) {
        currentMetrics.bmi = latest.bmi;
      }
      if (latest.muscle_mass_kg && previous.muscle_mass_kg) {
        currentMetrics.muscleMass = latest.muscle_mass_kg;
      }
    }

    return currentMetrics;
  };

  const loadWithingsMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await core.invoke("get_withings_measurements", {
        limit: 3,
      });

      if (result && Array.isArray(result)) {
        const measurements = result as WithingsMeasurement[];

        if (measurements.length > 0) {
          const latest = measurements[0];
          if (latest !== undefined) {
            const previous = measurements[1];
            const currentMetrics = processMeasurements(latest, previous);
            setMetrics(currentMetrics);
          }
        }
      }
    } catch {
      // NOTE: Error loading Withings metrics
      setError("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (value: null | number, previousValue: null | number) => {
    if (!value || !previousValue) {
      return <Minus className="h-4 w-4 text-slate-400" />;
    }

    if (value > previousValue) {
      return <TrendingUp className="h-4 w-4 text-green-500" />;
    } else if (value < previousValue) {
      return <TrendingDown className="h-4 w-4 text-red-500" />;
    } else {
      return <Minus className="h-4 w-4 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-slate-700 text-white">
        <div className="p-4">
          <div className="animate-pulse">
            <div className="h-4 bg-slate-700 rounded mb-2" />
            <div className="h-6 bg-slate-700 rounded mb-2" />
            <div className="h-4 bg-slate-700 rounded" />
          </div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-slate-800/50 border-slate-700 text-white">
        <div className="p-4 text-center">
          <div className="text-red-400 text-sm">{error}</div>
          <button
            className="mt-2 text-blue-400 hover:text-blue-300 text-xs"
            onClick={() => {
              void loadWithingsMetrics();
            }}
          >
            Erneut versuchen
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-800/50 border-slate-700 text-white hover:bg-slate-800/70 transition-all duration-200">
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-slate-300">Aktuelle Werte</h3>
          {metrics.lastUpdate ? (
            <span className="text-xs text-slate-500">
              {new Date(metrics.lastUpdate).toLocaleDateString("de-DE")}
            </span>
          ) : null}
        </div>

        <div className="space-y-3">
          {/* Gewicht */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Gewicht</span>
            <div className="flex items-center space-x-2">
              <span className="text-lg font-semibold text-white">
                {metrics.weight ? `${metrics.weight.toFixed(1)} kg` : "N/A"}
              </span>
              {getTrendIcon(metrics.weight, metrics.weight)}
            </div>
          </div>

          {/* BMI */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">BMI</span>
            <div className="flex items-center space-x-2">
              <span className="text-lg font-semibold text-white">
                {metrics.bmi ? metrics.bmi.toFixed(1) : "N/A"}
              </span>
              {getTrendIcon(metrics.bmi, metrics.bmi)}
            </div>
          </div>

          {/* Muskelmasse */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Muskelmasse</span>
            <div className="flex items-center space-x-2">
              <span className="text-lg font-semibold text-white">
                {metrics.muscleMass ? `${metrics.muscleMass.toFixed(1)} kg` : "N/A"}
              </span>
              {getTrendIcon(metrics.muscleMass, metrics.muscleMass)}
            </div>
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-slate-700">
          <button
            className="w-full text-xs text-blue-400 hover:text-blue-300 transition-colors"
            onClick={() => {
              void loadWithingsMetrics();
            }}
          >
            Aktualisieren
          </button>
        </div>
      </div>
    </Card>
  );
};
